import { useCallback, useEffect, useRef, useState } from "react";

import { MEMBERSHIP_EVENTS } from "src/components/Person/events";
import { useOrgDesignBetaEnabled } from "src/components/OrgChart/controller/dnd/featureFlags";
import { useWorkspace } from "src/contexts/global/WorkspaceContext";
import { useOrgDesignConfig } from "src/insights/useOrgDesignConfig";
import eventEmitter, { EVENTS } from "src/services/eventEmitter";
import {
  INSIGHTS_HIERARCHY_VIEW,
  resolveDefaultInsightsHierarchyView,
} from "src/util/insightsHierarchyView";

export const INSIGHTS_STRUCTURE_EVENTS = [
  EVENTS.TEAM_OWNER_CHANGED,
  MEMBERSHIP_EVENTS.MEMBERSHIP_CHANGED,
  EVENTS.NEW_GROUP_ADDED,
  EVENTS.GROUP_REMOVED,
  EVENTS.INSIGHTS_WORKSPACE_CONFIG_CHANGED,
];

export const useInsightsHierarchyView = () => {
  const workspace = useWorkspace();
  const workspaceId = workspace?.id;
  const odBetaEnabled = useOrgDesignBetaEnabled();
  const {
    ready,
    loading: orgDesignLoading,
    allowLensSwitching,
    defaultViewRaw: workspaceDefaultView,
  } = useOrgDesignConfig();

  // Reporting-line lens is OD beta (release + Enable OD beta). Release OD locks
  // to the supply / team-owners lens even if workspace config still has
  // allowLensSwitching from a prior beta session.
  const lensSwitchingEnabled = Boolean(allowLensSwitching && odBetaEnabled);

  const userSetHierarchyViewRef = useRef(false);
  const hierarchyWorkspaceIdRef = useRef(workspaceId);

  // null until workspace default is known — avoids a wrong first-paint query.
  const [hierarchyView, setHierarchyViewState] = useState<string | null>(null);

  const setHierarchyView = useCallback((view: string) => {
    userSetHierarchyViewRef.current = true;
    setHierarchyViewState(view);
  }, []);

  const defaultViewRaw =
    typeof workspaceDefaultView === "string" ||
    workspaceDefaultView === null ||
    workspaceDefaultView === undefined
      ? workspaceDefaultView
      : undefined;

  useEffect(() => {
    if (hierarchyWorkspaceIdRef.current !== workspaceId) {
      hierarchyWorkspaceIdRef.current = workspaceId;
      userSetHierarchyViewRef.current = false;
      setHierarchyViewState(null);
    }
    if (userSetHierarchyViewRef.current) return;
    if (!ready && orgDesignLoading) return;
    setHierarchyViewState(resolveDefaultInsightsHierarchyView(defaultViewRaw));
  }, [defaultViewRaw, workspaceId, ready, orgDesignLoading]);

  useEffect(() => {
    const onConfigChanged = (payload?: {
      defaultInsightsHierarchyView?: string | null;
    }) => {
      userSetHierarchyViewRef.current = false;
      setHierarchyViewState(
        resolveDefaultInsightsHierarchyView(
          payload?.defaultInsightsHierarchyView ?? defaultViewRaw
        )
      );
    };
    eventEmitter.on(EVENTS.INSIGHTS_WORKSPACE_CONFIG_CHANGED, onConfigChanged);
    return () => {
      eventEmitter.off(
        EVENTS.INSIGHTS_WORKSPACE_CONFIG_CHANGED,
        onConfigChanged
      );
    };
  }, [defaultViewRaw]);

  const effectiveHierarchyView = !lensSwitchingEnabled
    ? INSIGHTS_HIERARCHY_VIEW.SUPPLY
    : hierarchyView;

  return {
    hierarchyView: effectiveHierarchyView,
    setHierarchyView,
    lensSwitchingEnabled,
    hierarchyReady: Boolean(effectiveHierarchyView),
    orgDesignLoading,
    workspaceDefaultView: defaultViewRaw,
  };
};
